import { useQuery } from '@tanstack/react-query';
import { DesignModelDto } from 'src/api/FiretrolApiClient';
import { designModelApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { hasPollTickFailed, toCancelToken } from 'src/api/queries/queryClient';

const POLL_INTERVAL = 5000;

export const isConversionTerminal = (status: string | undefined | null) =>
  status === 'Ready' || status === 'Failed';

/**
 * Only an IFC upload puts a model into 'Processing'; CAD/FAB-only models keep
 * a null status forever, so polling is driven by 'Processing' rather than by
 * the absence of a terminal status.
 */
export const isConversionProcessing = (status: string | undefined | null) =>
  status === 'Processing';

/**
 * @registry Design models for a job. Polls while a model is converting so the
 * list's status column settles without a manual reload.
 */
export function useDesignModels(jobId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.designModels.list(jobId ?? -1),
    queryFn: ({ signal }) =>
      designModelApiClient.list(jobId!, toCancelToken(signal)),
    enabled: jobId != null && jobId > 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      if (hasPollTickFailed(query.state)) return false;
      const models = query.state.data as DesignModelDto[] | undefined;
      const converting = models?.some((model) =>
        isConversionProcessing(model.conversionStatus),
      );
      return converting ? POLL_INTERVAL : false;
    },
    meta: { silent: true, errorContext: 'design models' },
  });
}
